/**
 * Knowledge browser — spotlight-style.
 *
 * The dialog mounts as a centered search field with no chrome until the
 * user types. As soon as results arrive the list slides in beneath the
 * input; ↑/↓ moves the active row, Enter or click pins that row and
 * reveals the detail pane on the right (the dialog widens to accommodate
 * it). Escape, the close button, or backdrop click closes the dialog and
 * `close()` resets all UI state — reopening always lands on a fresh
 * spotlight, never on a stale selection.
 *
 * State split:
 *  - Server data lives in TanStack Query (cache survives close/reopen).
 *  - UI state (open, selected id, query, filters) lives in the dialog's
 *    Zustand store so the citation chip can deep-link straight to an
 *    entry id via `useKnowledgeBrowserStore.open(entryId)`.
 */

import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import {
  alpha,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { format as formatSql } from 'sql-formatter';

import { ArtifactSharingControl } from './ArtifactSharingControl';
import { CodeWithKnowledgeLinks } from './CodeWithKnowledgeLinks';
import { DatasetChip } from './DatasetChip';
import { Markdown } from './Markdown';
import {
  useKnowledgeEntry,
  useKnowledgeFilterOptions,
  useSearchKnowledge,
} from '../api/knowledgeQueries';
import type { KnowledgeEntry, KnowledgeKind, KnowledgeSearchResult } from '../api/knowledgeTypes';
import { useKnowledgeBrowserStore } from '../state/knowledgeBrowserStore';
import type { EpiArtifactScope } from '../types/epi-chat-types';

const PAYLOAD_INDENT = 2;

/** Debounce window for the corpus search input (ms). */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Tiny inline debounce hook. Pulled local to the dialog because no
 * other feature needs it yet; promote to ``@verantos-dev/core-utils``
 * if a second consumer appears.
 */
const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

const KIND_LABEL: Record<KnowledgeKind, string> = {
  data_finding: 'Data finding',
  clinical_def: 'Clinical def',
  cohort_function: 'Cohort fn',
  snippet: 'Snippet',
  study: 'Study',
  av: 'Variable',
};

export interface KnowledgeBrowserDialogProps {
  /**
   * Pinned dataset for the active session, if any. Threaded through to the
   * search endpoint so dataset-scoped entries for the right schema appear,
   * matching the agent's recall semantics.
   */
  activeDataset?: string | null;
  /**
   * Active agent-session id. Threaded through to the search endpoint so
   * session-scoped entries the user has registered in this session
   * (typically analytical variables they're still iterating on) participate in the result
   * set without forcing the user to promote first.
   */
  activeSessionId?: string | null;
}

/** Pixel widths the dialog snaps between when the detail pane appears.
 *  Spotlight feel: compact when only the search column is visible; widens
 *  to a comfortable two-pane size once a result is committed. Both are
 *  capped at a viewport-relative max so the dialog always keeps comfortable
 *  side margins (`92vw`) — no edge-hugging even on narrow laptops. */
const COMPACT_WIDTH_PX = 720;
const EXPANDED_WIDTH_PX = 1180;
const RESULT_COLUMN_PX = 380;
/** Viewport-relative cap so the dialog always leaves side breathing room.
 *  `88vw` lands ~6% padding per side, which keeps the spotlight feeling
 *  centered and not edge-hugging at every reasonable viewport size. */
const MAX_WIDTH_VW = '88vw';

export const KnowledgeBrowserDialog = ({
  activeDataset = null,
  activeSessionId = null,
}: KnowledgeBrowserDialogProps) => {
  const isOpen = useKnowledgeBrowserStore((s) => s.isOpen);
  const close = useKnowledgeBrowserStore((s) => s.close);
  const query = useKnowledgeBrowserStore((s) => s.query);
  const setQuery = useKnowledgeBrowserStore((s) => s.setQuery);
  const kinds = useKnowledgeBrowserStore((s) => s.kinds);
  const toggleKind = useKnowledgeBrowserStore((s) => s.toggleKind);
  const setKinds = useKnowledgeBrowserStore((s) => s.setKinds);
  const selectedEntryId = useKnowledgeBrowserStore((s) => s.selectedEntryId);
  const selectEntry = useKnowledgeBrowserStore((s) => s.selectEntry);

  // Debounce the query that hits the search endpoint so each keystroke
  // doesn't fire a request. The Cortex-backed retriever has to embed
  // every query and run a similarity scan, so even at the modest corpus
  // size today a per-keystroke fan-out is wasteful and noisier in
  // SigNoz than it needs to be.
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const { data: filterOptions } = useKnowledgeFilterOptions({ enabled: isOpen });
  const search = useSearchKnowledge(
    {
      q: debouncedQuery,
      kinds: kinds.length > 0 ? kinds : undefined,
      activeDataset,
      sessionId: activeSessionId,
      limit: 25,
    },
    { enabled: isOpen }
  );
  const entry = useKnowledgeEntry(isOpen ? selectedEntryId : null);

  // Memoize so the `useCallback` for keydown nav keeps a stable identity
  // when `search.data` itself is stable (TanStack Query returns the same
  // reference on cache hits).
  const results = useMemo(() => search.data ?? [], [search.data]);

  // Stale-results handling. Because `useSearchKnowledge` uses TanStack
  // Query's `placeholderData: keepPreviousData`, the `results` array
  // can hold rows from a previous query key while a new fetch is in
  // flight — exactly the case the user flagged: typing a new query
  // leaves the old matches on screen instead of showing a loader.
  //
  // Two pending sub-states get different treatment so the UX feels
  // snappy without being dishonest:
  //
  //  - Appending case: the live query strictly extends the previous
  //    debounced query (e.g. user typed one more letter). Previous
  //    results are very likely still in the candidate set, so keep
  //    them visible (dimmed) with a loading bar at the top.
  //
  //  - Divergent case: deleted characters, jumped to a new word, etc.
  //    Previous results may have nothing to do with what the user now
  //    wants, so swap them out for a spinner.
  const trimmedLiveQuery = query.trim();
  const trimmedDebouncedQuery = debouncedQuery.trim();
  const queryDiffersFromFetched = trimmedLiveQuery !== trimmedDebouncedQuery;
  // `isPlaceholderData` is true when TanStack Query is returning the
  // previous-key's data while the new key's fetch is pending — the
  // authoritative signal that `results` is stale relative to the
  // active query key.
  const isShowingPlaceholder = search.isPlaceholderData === true;
  const isResultsPending = queryDiffersFromFetched || isShowingPlaceholder;
  const isAppendingQuery =
    isResultsPending &&
    trimmedLiveQuery.toLowerCase().startsWith(trimmedDebouncedQuery.toLowerCase()) &&
    trimmedDebouncedQuery.length > 0;
  // In the divergent case, treat the search as actively loading so the
  // ResultList renders the spinner branch instead of stale rows.
  const treatAsLoading =
    (search.isLoading && search.fetchStatus !== 'idle') || (isResultsPending && !isAppendingQuery);

  // Arrow-key navigation. We only handle ↑ / ↓ / Enter when there's a
  // result list to navigate; everything else falls through to the
  // TextField so typing still works. Arrow nav DOES commit selection
  // (matches the user-requested spotlight semantics: arrow or click both
  // open the detail pane), so each keystroke updates the store. TanStack
  // Query caches detail fetches by id, so repeated arrowing through
  // already-seen rows is free after the first pass.
  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (results.length === 0) return;
      const currentIndex = selectedEntryId
        ? results.findIndex((r) => r.id === selectedEntryId)
        : -1;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, results.length - 1);
        selectEntry(results[nextIndex].id);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
        selectEntry(results[nextIndex].id);
        return;
      }
      if (event.key === 'Enter') {
        // Enter on a row that's already selected is a no-op; on an unset
        // search the first row becomes the selection.
        event.preventDefault();
        if (currentIndex < 0) selectEntry(results[0].id);
      }
    },
    [results, selectedEntryId, selectEntry]
  );

  // Keep the highlighted row in view as the cursor moves. The scroll
  // container is the result list; we look up the row by data-testid id.
  const resultListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedEntryId) return;
    const container = resultListRef.current;
    if (!container) return;
    const row = container.querySelector<HTMLElement>(
      `[data-testid="knowledge-browser-result-${cssEscape(selectedEntryId)}"]`
    );
    // jsdom lacks `scrollIntoView`; guard so unit tests don't blow up.
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedEntryId]);

  const availableKinds = useMemo(
    () => filterOptions?.kinds ?? (Object.keys(KIND_LABEL) as KnowledgeKind[]),
    [filterOptions]
  );

  const detailOpen = selectedEntryId != null;
  const hasQuery = query.trim().length > 0;

  // Explicit focus on open. `autoFocus` on the TextField works the first
  // time the dialog mounts, but because we keep the dialog mounted across
  // close/reopen cycles (the store toggles `isOpen`), subsequent opens
  // can leave focus on the previously-focused page element. Drive it
  // imperatively whenever `isOpen` flips true so the user can always
  // start typing immediately.
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    // Defer one tick so MUI Dialog's own focus management has finished
    // its initial focus trap setup before we override.
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      data-testid="knowledge-browser-dialog"
      data-detail-open={detailOpen ? 'true' : 'false'}
      // Top-anchored layout: MUI Dialog centers its container vertically
      // by default, but the spotlight pattern (macOS Spotlight, Raycast,
      // Linear command palette) sits roughly a tenth of the viewport
      // down — close enough to feel attached to the top edge without
      // pinning to it. Override the container alignment + push the
      // Paper down by a fixed vh so it floats in roughly the same band
      // regardless of viewport size.
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
      // Spotlight-style frosted-glass surface: a touch of transparency
      // with backdrop blur so the dialog reads as a floating overlay
      // rather than a hard card. Falls back to a solid (slightly
      // translucent) fill on browsers without `backdrop-filter`.
      PaperProps={{
        sx: (theme) => ({
          width: detailOpen
            ? `min(${EXPANDED_WIDTH_PX}px, ${MAX_WIDTH_VW})`
            : `min(${COMPACT_WIDTH_PX}px, ${MAX_WIDTH_VW})`,
          maxWidth: 'none',
          // Anchor the spotlight ~10vh from the top of the viewport.
          // Tall enough to clear most app headers; short enough that the
          // spotlight feels prominent rather than floating mid-page.
          marginTop: '10vh',
          // Cap content height so the dialog never extends below the
          // viewport once offset from the top: 10vh top gap + 75vh body
          // + 15vh bottom breathing room. When the detail pane is open
          // we let the dialog fill that body height; otherwise the
          // compact spotlight sizes to its content (a few result rows).
          height: detailOpen ? '75vh' : 'auto',
          maxHeight: '75vh',
          transition:
            'width 220ms cubic-bezier(0.2, 0.8, 0.2, 1), height 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.background.paper, 0.88),
          backdropFilter: 'saturate(180%) blur(20px)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          // Slightly softer shadow than the MUI default — the spotlight
          // surface should feel weightless on top of the blurred backdrop.
          boxShadow: theme.shadows[10],
        }),
      }}
    >
      <Box
        sx={{
          display: 'grid',
          // Two columns when a detail is selected; collapse to a single
          // column for the bare spotlight state. The detail pane width
          // adapts down on small viewports (down to 60% of the dialog
          // body width) so the result column doesn't get squeezed
          // illegibly on a 13" laptop.
          gridTemplateColumns: detailOpen
            ? { xs: 'minmax(280px, 40%) 1fr', md: `${RESULT_COLUMN_PX}px 1fr` }
            : '1fr',
          minHeight: 0,
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            // `minWidth: 0` lets the column shrink past its content's
            // intrinsic width — load-bearing for keeping the spotlight
            // row content inside the dialog instead of bursting it
            // wider. See the matching note on the result-list Stack.
            minWidth: 0,
            borderRight: (theme) => (detailOpen ? `1px solid ${theme.palette.divider}` : 'none'),
          }}
        >
          <Box sx={{ paddingX: 3, paddingY: 2, paddingBottom: hasQuery ? 1 : 2 }}>
            <TextField
              fullWidth
              size="medium"
              inputRef={inputRef}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search the knowledge corpus…"
              inputProps={{
                'data-testid': 'knowledge-browser-search',
                'aria-label': 'Search knowledge corpus',
                // ARIA combobox-style cursor reference so screen
                // readers track the arrow-key cursor.
                'aria-controls': 'knowledge-browser-results',
                'aria-activedescendant': selectedEntryId
                  ? `knowledge-browser-result-${selectedEntryId}`
                  : undefined,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                // Clear-X appears only when there's text to clear, so
                // the empty input stays visually minimal. Clicking
                // resets the query (which also drops the current
                // selection via the store) and refocuses the field so
                // the user can immediately keep typing.
                endAdornment: hasQuery ? (
                  <InputAdornment position="end">
                    <Tooltip title="Clear search">
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        data-testid="knowledge-browser-clear"
                        onClick={() => {
                          setQuery('');
                          inputRef.current?.focus();
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
                sx: {
                  // Spotlight-style: larger type, no visible label,
                  // soft surface so the input reads as the primary
                  // focus point of the dialog.
                  fontSize: (theme) => theme.typography.h6.fontSize,
                  lineHeight: 1.4,
                  // Slight surface transparency on the input itself so
                  // the frosted-glass treatment continues through to
                  // the field background.
                  backgroundColor: (theme) => alpha(theme.palette.background.default, 0.6),
                },
              }}
            />
            {hasQuery && (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: 1 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em' }}
                  >
                    Filter by kind
                  </Typography>
                  {kinds.length > 0 && (
                    <Chip
                      size="small"
                      label="Clear"
                      variant="outlined"
                      onClick={() => setKinds([])}
                      data-testid="knowledge-browser-kind-clear"
                      sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
                    />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ marginTop: 0.5, flexWrap: 'wrap', gap: 0.5 }}
                  role="group"
                  aria-label="Filter results by knowledge kind"
                >
                  {availableKinds.map((kind) => {
                    const active = kinds.includes(kind);
                    return (
                      <Chip
                        key={kind}
                        label={KIND_LABEL[kind] ?? kind}
                        size="small"
                        variant={active ? 'filled' : 'outlined'}
                        color={active ? 'primary' : 'default'}
                        onClick={() => toggleKind(kind)}
                        onDelete={active ? () => toggleKind(kind) : undefined}
                        aria-pressed={active}
                        data-testid={`knowledge-browser-kind-${kind}`}
                      />
                    );
                  })}
                </Stack>
              </>
            )}
          </Box>
          {hasQuery && <Divider />}
          {/* Top-of-list progress bar that's only visible while we're
              keeping stale results around (the "appending" case). In the
              divergent case `treatAsLoading` flips the ResultList to its
              spinner branch instead, so this bar would be redundant. */}
          {hasQuery && isResultsPending && isAppendingQuery && (
            <LinearProgress data-testid="knowledge-browser-stale-progress" sx={{ height: 2 }} />
          )}
          <Box
            ref={resultListRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              // Mirror the column's minWidth constraint so the scroll
              // container can shrink with its parent rather than being
              // sized by the widest row's intrinsic content.
              minWidth: 0,
              // When the appending-but-pending state is showing, dim the
              // result rows a touch so the "these may update shortly"
              // intent is visible without hiding them outright.
              opacity: isResultsPending && isAppendingQuery ? 0.7 : 1,
              transition: 'opacity 150ms ease',
            }}
            id="knowledge-browser-results-region"
            aria-busy={isResultsPending ? 'true' : 'false'}
          >
            <ResultList
              query={query}
              isLoading={treatAsLoading}
              isError={search.isError}
              results={results}
              selectedEntryId={selectedEntryId}
              onSelect={selectEntry}
            />
          </Box>
        </Box>
        {detailOpen && (
          <Box
            sx={{
              paddingX: 3,
              paddingY: 2.5,
              overflow: 'auto',
              minHeight: 0,
              // The detail pane fades + slides in from the right when the
              // dialog widens. Keying the animation off `selectedEntryId`
              // resets it on row change so each new pick reads as a fresh
              // surface, not a stale one updating in place.
              '@keyframes knowledgeBrowserDetailIn': {
                from: { opacity: 0, transform: 'translateX(16px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
              animation: 'knowledgeBrowserDetailIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
            key={selectedEntryId}
          >
            <DetailPane
              entryId={selectedEntryId}
              isLoading={entry.isLoading && entry.fetchStatus !== 'idle'}
              isError={entry.isError}
              data={entry.data ?? null}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

/**
 * Tiny CSS.escape polyfill scoped to the ids we use. Knowledge entry ids
 * are dot-separated (``av.<slug>.v<n>``) which CSS attribute selectors
 * don't need escaping for, but if the id ever contains a ``"`` we'd
 * blow up the selector — handle it defensively here rather than at
 * every callsite.
 */
const cssEscape = (value: string): string => value.replace(/"/g, '\\"');

interface ResultListProps {
  query: string;
  isLoading: boolean;
  isError: boolean;
  results: KnowledgeSearchResult[];
  selectedEntryId: string | null;
  onSelect: (entryId: string) => void;
}

const ResultList = ({
  query,
  isLoading,
  isError,
  results,
  selectedEntryId,
  onSelect,
}: ResultListProps) => {
  // Bare spotlight: render nothing until the user types. Keeps the dialog
  // compact at first open so the search field is the only thing on screen.
  if (query.trim().length === 0) {
    return null;
  }
  if (isLoading) {
    return (
      <Box
        data-testid="knowledge-browser-results-loading"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          padding: 4,
          minHeight: 200,
          color: 'text.secondary',
        }}
      >
        <CircularProgress size={22} />
        <Typography variant="caption">Searching corpus…</Typography>
      </Box>
    );
  }
  if (isError) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert severity="error">Knowledge search failed. Try again in a moment.</Alert>
      </Box>
    );
  }
  if (results.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No matches.
        </Typography>
      </Box>
    );
  }
  return (
    <Stack
      divider={<Divider />}
      id="knowledge-browser-results"
      data-testid="knowledge-browser-results"
      role="listbox"
      aria-label="Knowledge results"
      // `minWidth: 0` is load-bearing: without it, the title Typography
      // (when previously `noWrap`) would push the row width past the
      // parent grid cell because flex/grid items default to
      // `minWidth: auto`. We've since dropped `noWrap` in favour of
      // a 2-line clamp, but keeping the constraint here also stops any
      // future inline-content (long code spans inside snippets, etc.)
      // from blowing the dialog horizontally.
      sx={{ minWidth: 0 }}
    >
      {results.map((row) => {
        const isSelected = row.id === selectedEntryId;
        return (
          <Box
            key={row.id}
            id={`knowledge-browser-result-${row.id}`}
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(row.id)}
            data-testid={`knowledge-browser-result-${row.id}`}
            sx={{
              paddingX: 3,
              paddingY: 1.5,
              cursor: 'pointer',
              // Two defensive constraints so rows can never overflow
              // the dialog horizontally regardless of content shape.
              minWidth: 0,
              overflow: 'hidden',
              backgroundColor: (theme) =>
                isSelected ? theme.palette.action.selected : 'transparent',
              '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                fontWeight: 600,
                letterSpacing: '0.06em',
                marginBottom: 0.25,
              }}
            >
              {(KIND_LABEL[row.kind] ?? row.kind).toUpperCase()} · {row.scope.toUpperCase()}
            </Typography>
            {/* Title clamps to two lines instead of single-line ellipsis:
                spotlight results often have long descriptive names
                ("Outpatient COPD exacerbations joined with OCS …") and
                single-line truncation made them hard to scan. Two lines
                surfaces enough to disambiguate; the body lives in the
                detail pane regardless. */}
            <Typography
              variant="subtitle1"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: 0.5,
                fontWeight: 600,
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {row.title}
            </Typography>
            <Box
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                color: (theme) => theme.palette.text.secondary,
                fontSize: (theme) => theme.typography.body2.fontSize,
                lineHeight: 1.45,
                '& p': { margin: 0 },
                '& strong': {
                  color: (theme) => theme.palette.text.primary,
                  // medium (500) keeps the inline emphasis readable but
                  // doesn't compete with the semibold title.
                  fontWeight: 500,
                },
              }}
            >
              <Markdown dense>{row.snippet}</Markdown>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

interface DetailPaneProps {
  entryId: string | null;
  isLoading: boolean;
  isError: boolean;
  data: KnowledgeEntry | null;
}

type DetailTab = 'body' | 'sql' | 'payload';

interface DetailTabsModel {
  available: DetailTab[];
  sql: string | null;
  /** Payload with the `sql` key stripped — what the Payload tab renders. */
  payloadWithoutSql: Record<string, unknown>;
}

// Candidate keys to look for SQL on, in priority order. Snippets and cohort
// functions store under `sql` (existing convention); the analytical variable-as-knowledge
// projection stores under `sql_body`. Probing in order keeps existing kinds
// rendering unchanged while letting analytical variable entries surface a SQL tab too.
const SQL_PAYLOAD_KEYS: readonly string[] = ['sql', 'sql_body'];

const buildDetailTabs = (entry: KnowledgeEntry): DetailTabsModel => {
  let sql: string | null = null;
  let sqlKey: string | null = null;
  for (const key of SQL_PAYLOAD_KEYS) {
    const candidate = entry.payload?.[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      sql = candidate;
      sqlKey = key;
      break;
    }
  }

  const payloadWithoutSql: Record<string, unknown> = { ...entry.payload };
  if (sqlKey) {
    delete payloadWithoutSql[sqlKey];
  }

  const available: DetailTab[] = ['body'];
  if (sql) available.push('sql');
  if (Object.keys(payloadWithoutSql).length > 0) available.push('payload');

  return { available, sql, payloadWithoutSql };
};

const formatSqlSafely = (sql: string): string => {
  try {
    return formatSql(sql, { language: 'snowflake', keywordCase: 'upper' });
  } catch {
    // The corpus contains the agent's draft fragments and templated
    // placeholders (`<DATASET_SCHEMA>`, `<TEMP_TABLE>`); when sql-formatter
    // can't parse one, render the original rather than dropping the body.
    return sql;
  }
};

const isAvScope = (scope: string): scope is EpiArtifactScope =>
  scope === 'session' || scope === 'user' || scope === 'org';

interface AvMeta {
  slug: string;
  outputType?: string;
  dataset?: string;
  sources: string[];
  status?: string;
  version: number | null;
  scope: EpiArtifactScope | null;
}

const readAvMeta = (data: KnowledgeEntry): AvMeta | null => {
  if (data.kind !== 'av') return null;
  const payload = data.payload ?? {};
  const slugRaw = payload['slug'];
  const slug =
    typeof slugRaw === 'string' && slugRaw.trim()
      ? slugRaw
      : data.id.replace(/^av\./, '').replace(/\.v\d+$/, '');
  const outputType =
    typeof payload['output_type'] === 'string' ? (payload['output_type'] as string) : undefined;
  const dataset =
    typeof payload['dataset_schema_built_against'] === 'string'
      ? (payload['dataset_schema_built_against'] as string)
      : undefined;
  const status = typeof payload['status'] === 'string' ? (payload['status'] as string) : undefined;
  const sourcesRaw = payload['sources'];
  const sources = Array.isArray(sourcesRaw)
    ? (sourcesRaw as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];
  const versionRaw = payload['version'];
  const version = typeof versionRaw === 'number' ? versionRaw : null;
  return {
    slug,
    outputType,
    dataset,
    sources,
    status,
    version,
    scope: isAvScope(data.scope) ? data.scope : null,
  };
};

const DetailPane = ({ entryId, isLoading, isError, data }: DetailPaneProps) => {
  // Pulled here so the analytical variable anchors row can re-select within the same dialog
  // when the user clicks another analytical variable's slug, mirroring the chip → entry
  // navigation pattern the corpus already uses elsewhere.
  const selectEntry = useKnowledgeBrowserStore((s) => s.selectEntry);
  const [activeTab, setActiveTab] = useState<DetailTab>('body');
  const tabs = useMemo<DetailTabsModel | null>(() => (data ? buildDetailTabs(data) : null), [data]);
  const avMeta = useMemo<AvMeta | null>(() => (data ? readAvMeta(data) : null), [data]);

  // Snap back to a valid tab whenever the entry (and so the available set)
  // changes — without this, switching from a snippet (sql tab) to a
  // clinical_def (no sql) leaves an empty pane behind. For analytical variable entries
  // default to the SQL tab when one exists, since the SQL body is the
  // primary thing the user opened the entry to read; the description
  // already shows above the tabs.
  useEffect(() => {
    if (!tabs) return;
    if (!tabs.available.includes(activeTab)) {
      const preferred = avMeta && tabs.available.includes('sql') ? 'sql' : tabs.available[0];
      setActiveTab(preferred ?? 'body');
    }
  }, [tabs, activeTab, avMeta]);

  if (!entryId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select an entry to view its full body.
      </Typography>
    );
  }
  if (isLoading) {
    return (
      <Box
        data-testid="knowledge-browser-detail-loading"
        sx={{
          // Vertically + horizontally center inside the detail pane so
          // the spinner reads as "this whole panel is busy" rather than
          // a stray element pinned to the top.
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          height: '100%',
          minHeight: 240,
          color: 'text.secondary',
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2">Loading entry…</Typography>
      </Box>
    );
  }
  if (isError || !data || !tabs) {
    return <Alert severity="error">Could not load knowledge entry.</Alert>;
  }

  return (
    <Box
      data-testid="knowledge-browser-detail"
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
    >
      {/* For analytical variable entries the scope chip below carries the visibility info,
          so the overline only names the kind + version (when frozen).
          Other kinds keep the scope_key (often a dataset name, which is
          load-bearing context). */}
      <Typography variant="overline" color="text.secondary">
        {avMeta
          ? `${KIND_LABEL[data.kind] ?? data.kind}${avMeta.version ? ` · v${avMeta.version}` : ''}`
          : `${KIND_LABEL[data.kind] ?? data.kind} · ${data.scope}${
              data.scopeKey ? ` · ${data.scopeKey}` : ''
            }`}
      </Typography>
      {/* Entry title — subtitle1 + semibold to match the analytical variable-name treatment on
          the artifact rail and SQL drawer header. h6 (20/medium) was a step
          larger than anywhere else in the chat surface. */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 0.5 }}>
        {data.title}
      </Typography>
      {/* analytical variable entries: description directly below the title (it's the most
          read piece of context), then a single facts strip combining
          output_type / status / dataset / scope. Sources appear as a
          quieter inline list. We deliberately do NOT render data.tags
          for analytical variables — those tags (av, <output_type>, <status>, version:N)
          are derived from the same payload fields already shown in the
          strip above, so rendering them again was pure noise.

          Other kinds (snippet, clinical_def, ...) skip this block and
          fall through to the original source + tags layout below. */}
      {avMeta ? (
        <Box sx={{ marginBottom: 1.5 }}>
          {data.intent && (
            <Box
              sx={{
                marginBottom: 1,
                color: (theme) => theme.palette.text.primary,
                fontSize: (theme) => theme.typography.body2.fontSize,
                lineHeight: 1.5,
                '& strong': { color: (theme) => theme.palette.text.primary, fontWeight: 600 },
                '& p': { margin: 0 },
              }}
            >
              <Markdown dense>{data.intent}</Markdown>
            </Box>
          )}
          {/* Two-row facts panel — same shape as the artifact card and SQL
              drawer so the user reads the same metadata in the same order
              regardless of which surface they're on. Row 1 = definition
              (output / status / dataset / anchors), row 2 = knowledge &
              sharing (citations / visibility). */}
          <Stack sx={{ gap: 0.5 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              flexWrap="wrap"
              sx={{ gap: 0.5 }}
            >
              {avMeta.outputType && (
                <Chip
                  size="small"
                  label={`output: ${avMeta.outputType}`}
                  variant="outlined"
                  sx={{ height: 22, fontSize: (theme) => theme.typography.caption.fontSize }}
                />
              )}
              {avMeta.status && avMeta.status !== 'drafting' && (
                <Chip
                  size="small"
                  label={`status: ${avMeta.status}`}
                  color={avMeta.status === 'blocked' ? 'error' : 'success'}
                  sx={{ height: 22, fontSize: (theme) => theme.typography.caption.fontSize }}
                />
              )}
              {avMeta.dataset && (
                <DatasetChip
                  dataset={avMeta.dataset}
                  testId={`knowledge-browser-dataset-${avMeta.dataset}`}
                />
              )}
              {avMeta.sources.length > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  flexWrap="wrap"
                  sx={{ gap: 0.25 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: '0.04em' }}
                  >
                    anchors:
                  </Typography>
                  {avMeta.sources.map((sourceSlug) => (
                    <Box
                      key={sourceSlug}
                      component="button"
                      onClick={() => selectEntry(`av.${sourceSlug}`)}
                      sx={{
                        border: 0,
                        padding: 0,
                        background: 'none',
                        cursor: 'pointer',
                        fontFamily: 'monospace',
                        fontSize: (theme) => theme.typography.caption.fontSize,
                        color: (theme) => theme.palette.primary.main,
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        '&:hover': { color: (theme) => theme.palette.primary.dark },
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 1,
                          borderRadius: 1,
                        },
                      }}
                      data-testid={`knowledge-browser-anchor-${sourceSlug}`}
                      aria-label={`Open anchor analytical variable ${sourceSlug}`}
                    >
                      {sourceSlug}
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
            {/* Sharing chip on its own row — same as the other surfaces.
                ``key`` forces a fresh mount per entry id so the
                in-progress mutation state from a previous entry's
                promote click doesn't leak. */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              flexWrap="wrap"
              sx={{ gap: 0.5 }}
            >
              <ArtifactSharingControl
                key={data.id}
                slug={avMeta.slug}
                scope={avMeta.scope ?? undefined}
                frozenVersion={avMeta.version}
              />
            </Stack>
          </Stack>
        </Box>
      ) : (
        <>
          {data.source && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ marginBottom: 1, wordBreak: 'break-all' }}
            >
              Source: {data.source}
            </Typography>
          )}
          {data.tags.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flexWrap: 'wrap', gap: 0.5, marginBottom: 1 }}
            >
              {data.tags.map((tag) => (
                <Chip key={tag} size="small" label={tag} variant="outlined" />
              ))}
            </Stack>
          )}
        </>
      )}
      <Tabs
        value={activeTab}
        onChange={(_, value: DetailTab) => setActiveTab(value)}
        sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, minHeight: 36 }}
      >
        {tabs.available.includes('body') && (
          <Tab
            value="body"
            label="Body"
            data-testid="knowledge-browser-tab-body"
            sx={{ minHeight: 36 }}
          />
        )}
        {tabs.available.includes('sql') && (
          <Tab
            value="sql"
            label="SQL"
            data-testid="knowledge-browser-tab-sql"
            sx={{ minHeight: 36 }}
          />
        )}
        {tabs.available.includes('payload') && (
          <Tab
            value="payload"
            label="Payload"
            data-testid="knowledge-browser-tab-payload"
            sx={{ minHeight: 36 }}
          />
        )}
      </Tabs>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, paddingTop: 2 }}>
        {activeTab === 'body' && (
          <Box data-testid="knowledge-browser-tabpanel-body">
            <Markdown>{data.intent}</Markdown>
          </Box>
        )}
        {activeTab === 'sql' && tabs.sql && <SqlTabPanel sql={tabs.sql} />}
        {activeTab === 'payload' && <PayloadTabPanel payload={tabs.payloadWithoutSql} />}
      </Box>
    </Box>
  );
};

const SqlTabPanel = ({ sql }: { sql: string }) => {
  const formatted = useMemo(() => formatSqlSafely(sql), [sql]);
  const handleCopy = () => {
    void navigator.clipboard?.writeText(formatted);
  };
  return (
    <Box
      data-testid="knowledge-browser-tabpanel-sql"
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ marginBottom: 0.5 }}>
        <Tooltip title="Copy SQL">
          <IconButton size="small" onClick={handleCopy} aria-label="Copy SQL">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <CodeWithKnowledgeLinks
          language="sql"
          wrapLongLines
          code={formatted}
          customStyle={{
            margin: 0,
            padding: '12px',
            fontSize: '1rem',
            lineHeight: 1.55,
            background: 'transparent',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * Renders the residual payload (everything except the SQL body, which has
 * its own tab) as syntax-highlighted JSON. Pretty-printed via
 * ``JSON.stringify`` with the shared 2-space indent so the structure is
 * readable, then handed to the same Prism highlighter the SQL tab uses
 * — same look-and-feel across both code panes.
 */
const PayloadTabPanel = ({ payload }: { payload: Record<string, unknown> }) => {
  const formatted = useMemo(() => JSON.stringify(payload, null, PAYLOAD_INDENT), [payload]);
  const handleCopy = () => {
    void navigator.clipboard?.writeText(formatted);
  };
  return (
    <Box
      data-testid="knowledge-browser-tabpanel-payload"
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ marginBottom: 0.5 }}>
        <Tooltip title="Copy JSON">
          <IconButton size="small" onClick={handleCopy} aria-label="Copy JSON">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <CodeWithKnowledgeLinks
          language="json"
          wrapLongLines
          code={formatted}
          customStyle={{
            margin: 0,
            padding: '12px',
            fontSize: '1rem',
            lineHeight: 1.55,
            background: 'transparent',
          }}
        />
      </Box>
    </Box>
  );
};
